import { useEffect } from "react";

// Reference-counted body-scroll lock, shared by every open overlay.
//
// Two reasons it is NOT a plain save/restore inside the effect below:
//  1. Callers legitimately SWAP `onEscape` while open (`busy ? null : onClose`,
//     and the library picker disabling Escape while its preview is stacked). A
//     single effect keyed on `[open, onEscape]` would tear the lock down and
//     re-establish it on that swap — and when another overlay is already open,
//     it would capture the ALREADY-LOCKED "hidden" as its restore value, so the
//     page stayed unscrollable forever after close.
//  2. Stacked overlays unmounting in the same commit run their cleanups in tree
//     order, not reverse order, so the last writer won regardless of nesting.
// Counting makes both cases order-independent: only the first acquire saves the
// real baseline, only the last release restores it.
let lockCount = 0;
let savedOverflow = "";
let savedPaddingRight = "";

function acquireScrollLock(): () => void {
  if (lockCount === 0) {
    savedOverflow = document.body.style.overflow;
    savedPaddingRight = document.body.style.paddingRight;
    // QA-9 (pre-ship 2026-10-10): hiding the overflow removes the page's
    // scrollbar, and everything slid ~7 px sideways under the backdrop. Its
    // width becomes padding for as long as the lock holds. (0 when there is no
    // scrollbar — an overlay scrollbar, a page that does not scroll, jsdom.)
    const root = document.documentElement;
    const scrollbar = root.clientWidth > 0 ? window.innerWidth - root.clientWidth : 0;
    if (scrollbar > 0) {
      const base = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${base + scrollbar}px`;
    }
    document.body.style.overflow = "hidden";
  }
  lockCount += 1;
  let released = false;
  return () => {
    if (released) return; // a double-invoked cleanup must not underflow
    released = true;
    lockCount -= 1;
    if (lockCount === 0) {
      document.body.style.overflow = savedOverflow;
      document.body.style.paddingRight = savedPaddingRight;
    }
  };
}

/**
 * Mount-time effect bundle shared by every hand-built modal in /admin:
 *   - locks <body> scroll while the modal is open (restores the prior value
 *     on close — never assumes "" was the baseline)
 *   - listens for Escape and dispatches to the supplied callback
 *
 * Pass `onEscape: null` to disable the Escape branch — useful for in-flight
 * sends, gated blockers, anything that shouldn't be cancellable mid-flow.
 * The body-scroll lock applies regardless.
 *
 * Does NOT manage focus trap or backdrop clicks — those vary too much across
 * the existing modals to abstract safely. Callers keep their own backdrop
 * handler.
 *
 * Lives under `lib/` to match the existing `useUnsavedChangesGuard` location
 * (the project doesn't have a `hooks/` folder).
 */
export function useModalOverlay(opts: {
  open: boolean;
  onEscape: (() => void) | null;
}): void {
  const { open, onEscape } = opts;
  // Keyed on `open` ALONE — swapping `onEscape` must not disturb the lock.
  useEffect(() => {
    if (!open) return;
    return acquireScrollLock();
  }, [open]);

  useEffect(() => {
    if (!open || !onEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEscape();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onEscape]);
}
